import {CommonModule, DecimalPipe} from '@angular/common';
import {Component, inject, OnInit} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {catchError, forkJoin, of} from 'rxjs';
import {
  ApexAxisChartSeries,
  ApexChart,
  ApexDataLabels,
  ApexGrid,
  ApexLegend,
  ApexStroke,
  ApexTooltip,
  ApexXAxis,
  ApexYAxis,
  NgApexchartsModule
} from 'ng-apexcharts';
import {MaterialModule} from '../../../material.module';
import {
  ProductCardAnalyticsBreakdown,
  ProductCardAnalyticsMetrics,
  ProductCardAnalyticsProductMetric,
  ProductCardAnalyticsReport,
  ProductCardAnalyticsReportGrain,
  ProductCardAnalyticsSortKey,
  ShineAiReportsService
} from '../../../services/ai/shine-ai-reports.service';
import {ChatbotsService} from '../../../services/objects/chatbots.service';
import {AssistantsService} from '../../../services/objects/assistants.service';
import {IChatbotOptions} from '../../../models/chatbot-options.model';
import {IAssistantOptions} from '../../../models/assistant-options.model';

type RangePreset = '7d' | '30d' | '90d' | 'custom';

interface ProductCardAnalyticsKpi {
  label: string;
  value: string;
  hint: string;
}

interface ProductCardAnalyticsKpiRow {
  title: string;
  kpis: ProductCardAnalyticsKpi[];
}

interface ProductCardAnalyticsProductRow {
  productKey: string;
  skuLabel: string;
  skuFallbackLabel: string;
  nameLabel: string;
  nameFallbackLabel: string;
  cardIdLabel: string;
  displayed: number;
  viewed: number;
  hovered: number;
  clicks: number;
  viewRate: number;
  clickThroughRateByDisplay: number;
  clickThroughRateByView: number;
}

interface ProductCardAnalyticsBreakdownSection {
  title: string;
  rows: ProductCardAnalyticsBreakdownRow[];
}

interface ProductCardAnalyticsBreakdownRow {
  key: string;
  label: string;
  detail: string;
  displayed: number;
  viewed: number;
  clicks: number;
  viewRate: number;
  clickThroughRateByDisplay: number;
}

@Component({
  selector: 'app-product-card-analytics',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MaterialModule,
    NgApexchartsModule
  ],
  providers: [DecimalPipe],
  templateUrl: './product-card-analytics.component.html',
  styleUrls: ['./product-card-analytics.component.scss']
})
export class ProductCardAnalyticsComponent implements OnInit {
  private readonly reportsService = inject(ShineAiReportsService);
  private readonly chatbotsService = inject(ChatbotsService);
  private readonly assistantsService = inject(AssistantsService);
  private readonly decimalPipe = inject(DecimalPipe);

  readonly ranges: {label: string; value: RangePreset}[] = [
    {label: '7 days', value: '7d'},
    {label: '30 days', value: '30d'},
    {label: '90 days', value: '90d'},
    {label: 'Custom', value: 'custom'}
  ];
  readonly grains: {label: string; value: ProductCardAnalyticsReportGrain}[] = [
    {label: 'Daily', value: 'Day'},
    {label: 'Weekly', value: 'Week'},
    {label: 'Monthly', value: 'Month'}
  ];
  readonly sourceOptions = [
    {label: 'All sources', value: ''},
    {label: 'Live', value: 'live'},
    {label: 'History', value: 'history'}
  ];
  readonly cardTypeOptions = [
    {label: 'All card types', value: ''},
    {label: 'Product', value: 'product'},
    {label: 'Recommendation', value: 'recommendation'},
    {label: 'Unknown', value: 'unknown'}
  ];
  readonly presentationOptions = [
    {label: 'All presentations', value: ''},
    {label: 'Horizontal', value: 'horizontal'},
    {label: 'Vertical list', value: 'vertical-list'},
    {label: 'Vertical carousel', value: 'vertical-carousel'},
    {label: 'Compact carousel', value: 'compact-carousel'}
  ];
  readonly productColumns = ['sku', 'name', 'displayed', 'viewed', 'hovered', 'clicks', 'viewRate', 'ctrDisplay', 'ctrView'];

  selectedRange: RangePreset = '30d';
  startDate = '';
  endDate = '';
  selectedGrain: ProductCardAnalyticsReportGrain = 'Day';
  chatbotId = '';
  assistantId = '';
  productCardType = '';
  presentation = '';
  productSearch = '';
  eventSource = '';
  productSort: ProductCardAnalyticsSortKey = 'clicks';

  loading = false;
  errorMessage = '';
  report: ProductCardAnalyticsReport | null = null;
  kpiRows: ProductCardAnalyticsKpiRow[] = [];
  productRows: ProductCardAnalyticsProductRow[] = [];
  breakdowns: ProductCardAnalyticsBreakdownSection[] = [];
  chatbotNameById: Record<string, string> = {};
  assistantNameById: Record<string, string> = {};

  trendSeries: ApexAxisChartSeries = [];
  trendChart: ApexChart = {type: 'area', height: 320, toolbar: {show: false}, zoom: {enabled: false}};
  trendXAxis: ApexXAxis = {categories: []};
  trendYAxis: ApexYAxis = {min: 0, labels: {formatter: (value: number) => value.toFixed(0)}};
  trendStroke: ApexStroke = {curve: 'smooth', width: 3};
  trendDataLabels: ApexDataLabels = {enabled: false};
  trendLegend: ApexLegend = {position: 'top', horizontalAlign: 'left'};
  trendTooltip: ApexTooltip = {y: {formatter: (value: number) => this.formatNumber(value)}};
  trendGrid: ApexGrid = {borderColor: '#e8edf3', strokeDashArray: 4};

  ngOnInit(): void {
    this.loadEntityLabels();
    this.applyQuickRange('30d');
  }

  applyQuickRange(range: RangePreset): void {
    this.selectedRange = range;
    if (range === 'custom') {
      return;
    }

    const days = range === '7d' ? 7 : range === '90d' ? 90 : 30;
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - (days - 1));
    this.startDate = this.toDateInputValue(start);
    this.endDate = this.toDateInputValue(end);
    this.loadReport();
  }

  applyFilters(): void {
    if (!this.startDate || !this.endDate) {
      return;
    }

    this.selectedRange = 'custom';
    this.normalizeDateRange();
    this.loadReport();
  }

  clearFilters(): void {
    this.chatbotId = '';
    this.assistantId = '';
    this.productCardType = '';
    this.presentation = '';
    this.productSearch = '';
    this.eventSource = '';
    this.applyQuickRange('30d');
  }

  applyProductSort(sort: ProductCardAnalyticsSortKey): void {
    this.productSort = sort;
    this.productRows = this.sortProducts(this.productRows);
  }

  trackKpi(_: number, kpi: ProductCardAnalyticsKpi): string {
    return kpi.label;
  }

  trackKpiRow(_: number, row: ProductCardAnalyticsKpiRow): string {
    return row.title;
  }

  trackBreakdown(_: number, section: ProductCardAnalyticsBreakdownSection): string {
    return section.title;
  }

  trackBreakdownRow(_: number, row: ProductCardAnalyticsBreakdownRow): string {
    return row.key;
  }

  trackProduct(_: number, row: ProductCardAnalyticsProductRow): string {
    return row.productKey;
  }

  hasData(): boolean {
    return !!this.report && (
      this.report.totals.displayed > 0 ||
      this.report.totals.viewed > 0 ||
      this.report.totals.hovered > 0 ||
      this.report.totals.clicks > 0 ||
      this.productRows.length > 0
    );
  }

  private loadReport(): void {
    this.loading = true;
    this.errorMessage = '';

    this.reportsService.getProductCardAnalytics({
      startDate: this.startDate,
      endDate: this.endDate,
      grain: this.selectedGrain,
      chatbotId: this.chatbotId,
      assistantId: this.assistantId,
      productCardType: this.productCardType,
      presentation: this.presentation,
      productSearch: this.productSearch,
      eventSource: this.eventSource
    }).subscribe({
      next: report => {
        this.setReport(report);
        this.loading = false;
      },
      error: () => {
        this.report = null;
        this.kpiRows = [];
        this.productRows = [];
        this.breakdowns = [];
        this.trendSeries = [];
        this.trendXAxis = {categories: []};
        this.loading = false;
        this.errorMessage = 'Product-card analytics could not be loaded. Check the filters or try a shorter date range.';
      }
    });
  }

  private setReport(report: ProductCardAnalyticsReport): void {
    this.report = report;
    this.refreshReportViews();
  }

  private refreshReportViews(): void {
    const report = this.report;
    if (!report) {
      return;
    }

    this.kpiRows = this.buildKpiRows(report.totals);
    this.trendSeries = [
      {name: 'Displayed', data: report.trend.map(point => point.metrics.displayed)},
      {name: 'Viewed', data: report.trend.map(point => point.metrics.viewed)},
      {name: 'Hovered', data: report.trend.map(point => point.metrics.hovered)},
      {name: 'Clicks', data: report.trend.map(point => point.metrics.clicks)}
    ];
    this.trendXAxis = {
      categories: report.trend.map(point => point.periodLabel),
      labels: {rotate: -35, trim: true}
    };
    this.breakdowns = [
      this.breakdownSection('Chatbots', report.chatbots, this.chatbotNameById),
      this.breakdownSection('Assistants', report.assistants, this.assistantNameById),
      this.breakdownSection('Card types', report.productCardTypes),
      this.breakdownSection('Presentations', report.presentations),
      this.breakdownSection('Input paths', report.inputPaths || []),
      this.breakdownSection('Authorities', report.authorities || []),
      this.breakdownSection('Sources (hash only)', report.sources || [])
    ];
    this.productRows = this.sortProducts(report.products.map(product => this.toProductRow(product)));
  }

  private loadEntityLabels(): void {
    forkJoin({
      chatbots: this.chatbotsService.getAllAsync().pipe(catchError(() => of([] as IChatbotOptions[]))),
      assistants: this.assistantsService.getAllAsync().pipe(catchError(() => of([] as IAssistantOptions[])))
    }).subscribe(({chatbots, assistants}) => {
      this.chatbotNameById = this.buildChatbotNameMap(chatbots);
      this.assistantNameById = this.buildAssistantNameMap(assistants);
      this.refreshReportViews();
    });
  }

  private buildKpiRows(totals: ProductCardAnalyticsMetrics): ProductCardAnalyticsKpiRow[] {
    return [
      {
        title: 'Volume',
        kpis: [
          {label: 'Displayed', value: this.formatNumber(totals.displayed), hint: 'Cards rendered by the widget'},
          {label: 'Viewed', value: this.formatNumber(totals.viewed), hint: `${this.formatRate(totals.viewRate)} view rate`},
          {label: 'Hovered', value: this.formatNumber(totals.hovered), hint: 'Pointer engagement events'},
          {label: 'Clicks', value: this.formatNumber(totals.clicks), hint: 'Product-card click events'}
        ]
      },
      {
        title: 'Rates',
        kpis: [
          {label: 'View rate', value: this.formatRate(totals.viewRate), hint: 'Viewed divided by displayed'},
          {label: 'CTR display', value: this.formatRate(totals.clickThroughRateByDisplay), hint: 'Clicks divided by displayed'},
          {label: 'CTR view', value: this.formatRate(totals.clickThroughRateByView), hint: 'Clicks divided by viewed'}
        ]
      },
      {
        title: 'Coverage',
        kpis: [
          {label: 'Unique products', value: this.formatNumber(totals.uniqueProducts), hint: 'Distinct product identities'},
          {label: 'Card conversations', value: this.formatNumber(totals.productCardConversations), hint: 'Conversations with card activity'}
        ]
      }
    ];
  }

  private breakdownSection(
    title: string,
    rows: ProductCardAnalyticsBreakdown[],
    labelMap: Record<string, string> = {}
  ): ProductCardAnalyticsBreakdownSection {
    return {
      title,
      rows: rows
        .map(row => this.toBreakdownRow(row, labelMap))
        .sort((a, b) => b.clicks - a.clicks || b.viewed - a.viewed || a.label.localeCompare(b.label))
        .slice(0, 8)
    };
  }

  private toBreakdownRow(
    row: ProductCardAnalyticsBreakdown,
    labelMap: Record<string, string>
  ): ProductCardAnalyticsBreakdownRow {
    const key = this.dimensionLabel(row.key);
    const label = this.firstText(row.label, row.key ? labelMap[row.key] : null, key);
    return {
      key,
      label,
      detail: label !== key ? key : '',
      displayed: row.metrics.displayed,
      viewed: row.metrics.viewed,
      clicks: row.metrics.clicks,
      viewRate: row.metrics.viewRate,
      clickThroughRateByDisplay: row.metrics.clickThroughRateByDisplay
    };
  }

  private toProductRow(product: ProductCardAnalyticsProductMetric): ProductCardAnalyticsProductRow {
    const skuFallbackLabel = this.hashLabel('SKU hash', product.skuHash);
    const nameFallbackLabel = this.hashLabel('Name hash', product.productNameHash);
    return {
      productKey: product.productKey,
      skuLabel: this.firstText(product.sku, skuFallbackLabel),
      skuFallbackLabel,
      nameLabel: this.firstText(product.productName, product.label, product.name, nameFallbackLabel),
      nameFallbackLabel,
      cardIdLabel: this.hashLabel('Card hash', product.productCardId),
      displayed: product.metrics.displayed,
      viewed: product.metrics.viewed,
      hovered: product.metrics.hovered,
      clicks: product.metrics.clicks,
      viewRate: product.metrics.viewRate,
      clickThroughRateByDisplay: product.metrics.clickThroughRateByDisplay,
      clickThroughRateByView: product.metrics.clickThroughRateByView
    };
  }

  private sortProducts(rows: ProductCardAnalyticsProductRow[]): ProductCardAnalyticsProductRow[] {
    return [...rows].sort((a, b) =>
      b[this.productSort] - a[this.productSort] ||
      b.clicks - a.clicks ||
      b.viewed - a.viewed ||
      a.productKey.localeCompare(b.productKey)
    );
  }

  private hashLabel(label: string, value: string | null): string {
    if (!value) {
      return 'Not captured';
    }

    const trimmed = value.trim();
    return trimmed.length > 18 ? `${label} ${trimmed.slice(0, 12)}...${trimmed.slice(-6)}` : `${label} ${trimmed}`;
  }

  private buildChatbotNameMap(chatbots: IChatbotOptions[]): Record<string, string> {
    return (chatbots || []).reduce<Record<string, string>>((acc, chatbot) => {
      const label = this.firstText(chatbot.name, chatbot.frontEndOptions?.name, chatbot.id);
      if (chatbot.id) {
        acc[chatbot.id] = label;
      }
      return acc;
    }, {});
  }

  private buildAssistantNameMap(assistants: IAssistantOptions[]): Record<string, string> {
    return (assistants || []).reduce<Record<string, string>>((acc, assistant) => {
      const label = this.firstText(assistant.name, assistant.assistantId, assistant.id);
      if (assistant.id) {
        acc[assistant.id] = label;
      }
      if (assistant.assistantId) {
        acc[assistant.assistantId] = label;
      }
      return acc;
    }, {});
  }

  private firstText(...values: Array<string | null | undefined>): string {
    return values
      .map(value => `${value ?? ''}`.trim())
      .find(value => !!value) || '';
  }

  private dimensionLabel(value: string | null): string {
    const trimmed = `${value ?? ''}`.trim();
    return trimmed || 'Unknown';
  }

  private normalizeDateRange(): void {
    if (this.startDate > this.endDate) {
      const previousStart = this.startDate;
      this.startDate = this.endDate;
      this.endDate = previousStart;
    }
  }

  private toDateInputValue(date: Date): string {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  formatNumber(value: number): string {
    return this.decimalPipe.transform(value, '1.0-0') ?? '0';
  }

  formatRate(value: number): string {
    return `${this.decimalPipe.transform(value, '1.0-1') ?? '0'}%`;
  }
}
